import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerJournalCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type {
  JournalEntryDto,
  PostJournalEntryRequestDto,
} from "@/features/ledger/api/dto";

/**
 * Comptabilise une opération (`POST /JournalEntries/post`, guide §6.4) —
 * étape 2 du flux transaction : résout le modèle d'imputation actif du
 * couple code produit / provider et ventile `amounts` ; refuse toute
 * écriture déséquilibrée.
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7) : le scope garde la même clé
 * entre les retries automatiques de la MÊME tentative ; sur 422 (même clé,
 * corps différent) on régénère la clé (nouvelle tentative métier).
 */
export const usePostJournalEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "journal-entries", "post"],
    mutationFn: (body: PostJournalEntryRequestDto) =>
      mutateIdempotent<JournalEntryDto>({
        scopeId: "ledger.journal-entries.post",
        url: ledgerEndpoints.journalEntries.post,
        body,
      }),
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, ledgerJournalCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `usePostJournalEntry failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
